import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { Search, Heart, Utensils, Pizza, Flame, Fish, Coffee, ShoppingBag, Leaf, Beef, Wine, Palette, Sparkles, GlassWater, Monitor, Home, Star as StarIcon, Snowflake, Plus, ChevronLeft, ChevronRight, Menu, ShoppingCart, CheckCircle, CircleX, UtensilsCrossed } from 'lucide-react';
import MenuItemDetailModal from '../components/MenuItemDetailModal';
import CartSheet from '../components/ios/CartSheet';

function CategoryDetailPage() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const restaurantIdParam = searchParams.get('restaurantId');
  const businessTypeParam = searchParams.get('businessType') || 'food';
  const navigate = useNavigate();
  const { db, firebaseError } = useFirebase();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addToCart, setRestaurant, getItemCount, items: cartItems } = useCart();
  const { currentUser } = useAuth();
  
  const [searchText, setSearchText] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showCartSheet, setShowCartSheet] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isAppOffline, setIsAppOffline] = useState(false);

  // iOS MenuItemDetailView .task — quick-add is gated on the app's
  // online/offline flag, same as the item detail modal.
  useEffect(() => {
    if (!db) return;
    const settingsRef = doc(db, 'settings', 'global');
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      setIsAppOffline(snapshot.exists() ? (snapshot.data().isAppOffline || false) : false);
    }, () => setIsAppOffline(false));
    return unsubscribe;
  }, [db]);
  const itemRefs = useRef({});

  // Get category icon info
  const getCategoryIcon = (category) => {
    const iconMap = {
      'Burgers': { icon: Utensils, color: '#dc2626' },
      'Chicken': { icon: Flame, color: '#dc2626' },
      'Pizza': { icon: Pizza, color: '#d97706' },
      'Drinks': { icon: GlassWater, color: '#06b6d4' },
      'Desserts': { icon: Coffee, color: '#92400e' },
      'Sides': { icon: Utensils, color: '#f97316' },
      'Breakfast': { icon: Coffee, color: '#92400e' },
      'Fries': { icon: Utensils, color: '#f97316' },
      'Nuggets': { icon: Flame, color: '#dc2626' },
      'Wraps': { icon: Utensils, color: '#dc2626' },
      'Salads': { icon: Leaf, color: '#15803d' },
      'Ice Cream': { icon: Coffee, color: '#92400e' },
      'Milkshakes': { icon: GlassWater, color: '#06b6d4' },
      'Appetizers': { icon: Utensils, color: '#b45309' },
      'Main Course': { icon: Utensils, color: '#b45309' },
      'Grill Specials': { icon: Flame, color: '#b45309' },
      'Ribs': { icon: Beef, color: '#b91c1c' },
      'Lobster': { icon: Fish, color: '#0369a1' },
      'Grilled Fish': { icon: Fish, color: '#0369a1' },
      'Steaks': { icon: Beef, color: '#b91c1c' },
      'Cocktails': { icon: Wine, color: '#1e40af' },
      'Rice Dishes': { icon: Utensils, color: '#f97316' },
      'Noodles': { icon: Utensils, color: '#f97316' },
      'Asian': { icon: Utensils, color: '#15803d' },
      'Fried Rice': { icon: Utensils, color: '#f97316' },
      'Dim Sum': { icon: Utensils, color: '#f97316' },
      'Soups': { icon: Utensils, color: '#f97316' },
      'Stir Fry': { icon: Utensils, color: '#f97316' },
      'Starters': { icon: Utensils, color: '#b45309' },
      'Chicken & Beef': { icon: Beef, color: '#b91c1c' },
      'Seafood': { icon: Fish, color: '#0369a1' },
      'Vegetarian': { icon: Leaf, color: '#15803d' },
      'Sushi & Specialty Rolls': { icon: Utensils, color: '#f97316' },
      'Bento & Sushi Platters': { icon: Utensils, color: '#f97316' },
      'Martinis & Cocktails': { icon: Wine, color: '#1e40af' },
      'Gluten-Free Items': { icon: Leaf, color: '#15803d' },
      'Sauces': { icon: Utensils, color: '#f97316' },
      'Curry': { icon: Utensils, color: '#f97316' },
      'Biryani': { icon: Utensils, color: '#f97316' },
      'Naan': { icon: Utensils, color: '#f97316' },
      'Roti': { icon: Utensils, color: '#d97706' },
      'Tandoori': { icon: Flame, color: '#dc2626' },
      'Tikka': { icon: Flame, color: '#dc2626' },
      'Samosas': { icon: Utensils, color: '#f97316' },
      'Pakoras': { icon: Utensils, color: '#f97316' },
      'Dal': { icon: Utensils, color: '#f97316' },
      'Paneer': { icon: Utensils, color: '#f97316' },
      'Vindaloo': { icon: Flame, color: '#dc2626' },
      'Korma': { icon: Utensils, color: '#f97316' },
      'Masala': { icon: Utensils, color: '#f97316' },
      'Chutney': { icon: Utensils, color: '#f97316' },
      'Raita': { icon: Utensils, color: '#f97316' },
      'Lassi': { icon: GlassWater, color: '#06b6d4' },
      'Gulab Jamun': { icon: Coffee, color: '#92400e' },
      'Kheer': { icon: Coffee, color: '#92400e' },
      'Grilled Items': { icon: Flame, color: '#b45309' },
      'Broil': { icon: Flame, color: '#b45309' },
      'Vegi Pizza': { icon: Pizza, color: '#d97706' },
      'Vegi Roti': { icon: Utensils, color: '#d97706' },
      'Vegi Soup': { icon: Utensils, color: '#15803d' },
      'Vegi Curry': { icon: Utensils, color: '#15803d' },
      'Vegi Burger': { icon: Utensils, color: '#15803d' },
      'Vegi Sandwich': { icon: Utensils, color: '#15803d' },
      'Vegi Wrap': { icon: Utensils, color: '#15803d' },
      'Vegi Pasta': { icon: Utensils, color: '#15803d' },
      'Vegi Salad': { icon: Leaf, color: '#15803d' },
      'Vegan': { icon: Leaf, color: '#15803d' },
      'Vegan Pizza': { icon: Pizza, color: '#15803d' },
      'Vegan Burger': { icon: Utensils, color: '#15803d' },
      'Vegan Desserts': { icon: Coffee, color: '#15803d' },
      'Tofu': { icon: Utensils, color: '#15803d' },
      'Tempeh': { icon: Utensils, color: '#15803d' },
      'Seitan': { icon: Utensils, color: '#15803d' },
      'Legumes': { icon: Leaf, color: '#15803d' },
      'Grains': { icon: Utensils, color: '#15803d' },
      'Plant-Based': { icon: Leaf, color: '#15803d' },
      'Snack Box': { icon: ShoppingBag, color: '#f97316' },
      'Snack Box Sandwich': { icon: Utensils, color: '#f97316' },
      'Snack Box Wings': { icon: Flame, color: '#dc2626' },
      'Johnny Cakes': { icon: Utensils, color: '#f97316' },
      'Combo Box': { icon: ShoppingBag, color: '#f97316' },
      'Breakfast Box': { icon: Coffee, color: '#92400e' },
      'Lunch Box': { icon: ShoppingBag, color: '#f97316' },
      'Party Box': { icon: ShoppingBag, color: '#f97316' },
      'Beauty': { icon: StarIcon, color: '#be185d' },
      'Hair Care': { icon: StarIcon, color: '#be185d' },
      'Skin Care': { icon: StarIcon, color: '#be185d' },
      'Nails': { icon: StarIcon, color: '#be185d' },
      'Makeup': { icon: StarIcon, color: '#be185d' },
      'Wigs': { icon: StarIcon, color: '#be185d' },
      'Weaves': { icon: StarIcon, color: '#be185d' },
      'Braids': { icon: StarIcon, color: '#be185d' },
      'Beauty Accessories': { icon: StarIcon, color: '#be185d' },
      'Hair Extensions': { icon: StarIcon, color: '#be185d' },
      'Lace Products': { icon: StarIcon, color: '#be185d' },
      'Edge Control': { icon: StarIcon, color: '#be185d' },
      'Hair Gel': { icon: StarIcon, color: '#be185d' },
      'Mousse': { icon: StarIcon, color: '#be185d' },
      'Hair Spray': { icon: StarIcon, color: '#be185d' },
      'Hair Color': { icon: StarIcon, color: '#be185d' },
      'Relaxers': { icon: StarIcon, color: '#be185d' },
      'Shampoos': { icon: StarIcon, color: '#be185d' },
      'Conditioners': { icon: StarIcon, color: '#be185d' },
      'Hair Masks': { icon: StarIcon, color: '#be185d' },
      'Hair Oil': { icon: StarIcon, color: '#be185d' },
      'Hair Products': { icon: StarIcon, color: '#be185d' },
      'Lace Wigs': { icon: StarIcon, color: '#be185d' },
      'Closures': { icon: StarIcon, color: '#be185d' },
      'Frontals': { icon: StarIcon, color: '#be185d' },
      'Wig Caps': { icon: StarIcon, color: '#be185d' },
      'Wig Accessories': { icon: StarIcon, color: '#be185d' },
      'Lace Glue': { icon: StarIcon, color: '#be185d' },
      'Lace Remover': { icon: StarIcon, color: '#be185d' },
      'Wig Maintenance': { icon: StarIcon, color: '#be185d' },
      'Nail Polish': { icon: StarIcon, color: '#be185d' },
      'Acrylics': { icon: StarIcon, color: '#be185d' },
      'Gel Polish': { icon: StarIcon, color: '#be185d' },
      'Nail Tips': { icon: StarIcon, color: '#be185d' },
      'Nail Tools': { icon: StarIcon, color: '#be185d' },
      'Nail Art': { icon: StarIcon, color: '#be185d' },
      'Foundation': { icon: StarIcon, color: '#be185d' },
      'Concealer': { icon: StarIcon, color: '#be185d' },
      'Powder': { icon: StarIcon, color: '#be185d' },
      'Lipstick': { icon: StarIcon, color: '#be185d' },
      'Lip Gloss': { icon: StarIcon, color: '#be185d' },
      'Eyeshadow': { icon: StarIcon, color: '#be185d' },
      'Makeup Brushes': { icon: StarIcon, color: '#be185d' },
      'Makeup Tools': { icon: StarIcon, color: '#be185d' },
      'Eyelashes': { icon: StarIcon, color: '#be185d' },
      'Lash Glue': { icon: StarIcon, color: '#be185d' },
      'Eyebrow Products': { icon: StarIcon, color: '#be185d' },
      'Cleansers': { icon: StarIcon, color: '#be185d' },
      'Moisturizers': { icon: StarIcon, color: '#be185d' },
      'Serums': { icon: StarIcon, color: '#be185d' },
      'Toners': { icon: StarIcon, color: '#be185d' },
      'Face Masks': { icon: StarIcon, color: '#be185d' },
      'Body Care': { icon: StarIcon, color: '#be185d' },
      'Perfume': { icon: Sparkles, color: '#be185d' },
      'Body Sprays': { icon: Sparkles, color: '#be185d' },
      'Barber Supplies': { icon: StarIcon, color: '#be185d' },
      'Beard Care': { icon: StarIcon, color: '#be185d' },
      'Shaving Products': { icon: StarIcon, color: '#be185d' },
      'Hair Tools': { icon: StarIcon, color: '#be185d' },
      'Flat Irons': { icon: StarIcon, color: '#be185d' },
      'Curling Irons': { icon: StarIcon, color: '#be185d' },
      'Blow Dryers': { icon: StarIcon, color: '#be185d' },
      'Clippers': { icon: StarIcon, color: '#be185d' },
      'Trimmers': { icon: StarIcon, color: '#be185d' },
      'FashionJewelry': { icon: Sparkles, color: '#d97706' },
      'Handbags': { icon: ShoppingBag, color: '#be185d' },
      'Fashion Accessories': { icon: Sparkles, color: '#be185d' },
      'Salon Supplies': { icon: StarIcon, color: '#be185d' },
      'Disinfectants': { icon: StarIcon, color: '#be185d' },
      'Salon Equipment': { icon: StarIcon, color: '#be185d' },
      'Clothing': { icon: ShoppingBag, color: '#dc2626' },
      'Underwear': { icon: ShoppingBag, color: '#dc2626' },
      'Shoes': { icon: ShoppingBag, color: '#dc2626' },
      'Accessories': { icon: ShoppingBag, color: '#dc2626' },
      'Sports & Outdoors': { icon: ShoppingBag, color: '#dc2626' },
      'Sandals': { icon: ShoppingBag, color: '#dc2626' },
      'Toys': { icon: ShoppingBag, color: '#dc2626' },
      'Books': { icon: ShoppingBag, color: '#dc2626' },
      'Farmer': { icon: Leaf, color: '#15803d' },
      'Herbs': { icon: Leaf, color: '#15803d' },
      'Spices': { icon: Utensils, color: '#f97316' },
      'Banana': { icon: Leaf, color: '#22c55e' },
      'Mango': { icon: Leaf, color: '#22c55e' },
      'Apple': { icon: Leaf, color: '#22c55e' },
      'Grape': { icon: Leaf, color: '#22c55e' },
      'Strawberry': { icon: Leaf, color: '#22c55e' },
      'Local Harvest': { icon: Leaf, color: '#15803d' },
      'Potatoes': { icon: Leaf, color: '#f97316' },
      'Lettuce': { icon: Leaf, color: '#22c55e' },
      'Cabbages': { icon: Leaf, color: '#22c55e' },
      'Yam': { icon: Leaf, color: '#f97316' },
      'Pepper': { icon: Flame, color: '#dc2626' },
      'Thyme': { icon: Leaf, color: '#15803d' },
      'Pineapple': { icon: Leaf, color: '#22c55e' },
      'Watermelon': { icon: Leaf, color: '#22c55e' },
      'Sugar Cane': { icon: Leaf, color: '#22c55e' },
      'Supermarket': { icon: ShoppingBag, color: '#dc2626' },
      'Dairy': { icon: ShoppingBag, color: '#dc2626' },
      'Bread': { icon: Coffee, color: '#92400e' },
      'Meat': { icon: Beef, color: '#b91c1c' },
      'Produce': { icon: Leaf, color: '#15803d' },
      'Household': { icon: Home, color: '#b45309' },
      'Beverages': { icon: GlassWater, color: '#06b6d4' },
      'Frozen': { icon: Snowflake, color: '#06b6d4' },
      'Canned': { icon: Utensils, color: '#dc2626' },
      'Snacks': { icon: Utensils, color: '#f97316' },
      'Condiments': { icon: Utensils, color: '#f97316' },
      'Cleaning': { icon: Home, color: '#b45309' },
      'Pet Food': { icon: Utensils, color: '#dc2626' },
      'Baby Products': { icon: Utensils, color: '#dc2626' },
      'Health': { icon: Leaf, color: '#15803d' },
      'Personal Care': { icon: StarIcon, color: '#be185d' },
      'Frozen Fish': { icon: Fish, color: '#0369a1' },
      'Frozen Meat': { icon: Beef, color: '#b91c1c' },
      'Oats': { icon: Utensils, color: '#f97316' },
      'Cornmeal': { icon: Utensils, color: '#f97316' },
      'Rice': { icon: Utensils, color: '#f97316' },
      'Flour': { icon: Utensils, color: '#f97316' },
      'Sugar': { icon: Utensils, color: '#f97316' },
      'Salt': { icon: Utensils, color: '#f97316' },
      'Beans': { icon: Utensils, color: '#f97316' },
      'Lentils': { icon: Utensils, color: '#f97316' },
      'Grains': { icon: Utensils, color: '#f97316' },
      'Cooking Oil': { icon: Utensils, color: '#f97316' },
      'Vinegar': { icon: Utensils, color: '#f97316' },
      'Cereals': { icon: Coffee, color: '#92400e' },
      'Laundry Detergent': { icon: Home, color: '#b45309' },
      'Fabric Softener': { icon: Home, color: '#b45309' },
      'Dishwashing Liquid': { icon: Home, color: '#b45309' },
      'Detergent': { icon: Home, color: '#b45309' },
      'Cafe': { icon: Coffee, color: '#92400e' },
      'Coffee': { icon: Coffee, color: '#92400e' },
      'Pastries': { icon: Coffee, color: '#92400e' },
      'Sandwiches': { icon: Utensils, color: '#dc2626' },
      'Beverages': { icon: GlassWater, color: '#06b6d4' },
      'Eggs': { icon: Utensils, color: '#f97316' },
      'Pancakes': { icon: Coffee, color: '#92400e' },
      'Waffles': { icon: Coffee, color: '#92400e' },
      'Toast': { icon: Coffee, color: '#92400e' },
      'Bacon': { icon: Beef, color: '#b91c1c' },
      'Sausage': { icon: Beef, color: '#b91c1c' },
      'Omelets': { icon: Utensils, color: '#f97316' },
      'French Toast': { icon: Coffee, color: '#92400e' },
      'Smoothies': { icon: GlassWater, color: '#06b6d4' },
      'Juice': { icon: GlassWater, color: '#06b6d4' },
      'Bakery': { icon: Coffee, color: '#92400e' },
      'Bread': { icon: Coffee, color: '#92400e' },
      'Cakes': { icon: Coffee, color: '#92400e' },
      'Pastries': { icon: Coffee, color: '#92400e' },
      'Cookies': { icon: Coffee, color: '#92400e' },
      'Muffins': { icon: Coffee, color: '#92400e' },
      'Donuts': { icon: Coffee, color: '#92400e' },
      'Pies': { icon: Coffee, color: '#92400e' },
      'Tarts': { icon: Coffee, color: '#92400e' },
      'Croissants': { icon: Coffee, color: '#92400e' },
      'Bagels': { icon: Coffee, color: '#92400e' },
      'Patties': { icon: Utensils, color: '#dc2626' },
      'Big Bread': { icon: Coffee, color: '#92400e' },
      'Small Bread': { icon: Coffee, color: '#92400e' },
      'Butter Bread': { icon: Coffee, color: '#92400e' },
      'Pork Bread': { icon: Coffee, color: '#92400e' },
      'Sliced Bread': { icon: Coffee, color: '#92400e' },
      'Wheat Bread': { icon: Coffee, color: '#92400e' },
      'Hamburger Bread': { icon: Coffee, color: '#92400e' },
      'Hot Dog Bread': { icon: Coffee, color: '#92400e' },
      'Chicken Buckets': { icon: Flame, color: '#dc2626' },
      'Chicken Pieces': { icon: Flame, color: '#dc2626' },
      'Chicken Tenders': { icon: Flame, color: '#dc2626' },
      'Wings': { icon: Flame, color: '#dc2626' },
      'KFC Sandwiches': { icon: Utensils, color: '#dc2626' },
      'KFC Bowls': { icon: Utensils, color: '#dc2626' },
      'KFC Specials': { icon: Flame, color: '#dc2626' },
      'Street Food': { icon: Utensils, color: '#f97316' },
      'Fried Chicken': { icon: Flame, color: '#dc2626' },
      'Wedges': { icon: Utensils, color: '#f97316' },
      'Fries': { icon: Utensils, color: '#f97316' },
      'Fried Fish': { icon: Fish, color: '#0369a1' },
      'Festival': { icon: Utensils, color: '#f97316' },
      'Jerk Chicken': { icon: Flame, color: '#dc2626' },
      'Jerk Pork': { icon: Beef, color: '#b91c1c' },
      'Jerk Ribs': { icon: Beef, color: '#b91c1c' },
      'Mixed': { icon: Utensils, color: '#f97316' },
      'Garlic Bread': { icon: Coffee, color: '#92400e' },
      'Bakes': { icon: Coffee, color: '#92400e' },
      'Coconut Water': { icon: GlassWater, color: '#06b6d4' },
      'Lemonade': { icon: GlassWater, color: '#06b6d4' },
      'Cane Juice': { icon: GlassWater, color: '#06b6d4' },
      'Mango Juice': { icon: GlassWater, color: '#06b6d4' },
      'Soursop Juice': { icon: GlassWater, color: '#06b6d4' },
      'Special Mix': { icon: GlassWater, color: '#06b6d4' },
      'Fruit Smoothies': { icon: GlassWater, color: '#06b6d4' },
      'Protein Smoothies': { icon: GlassWater, color: '#06b6d4' },
      'Local Crafts': { icon: Palette, color: '#b45309' },
      'Souvenirs': { icon: Palette, color: '#b45309' },
      'Handmade Goods': { icon: Palette, color: '#b45309' },
      'Local Art': { icon: Palette, color: '#b45309' },
      'Pottery': { icon: Palette, color: '#b45309' },
      'Woodwork': { icon: Palette, color: '#b45309' },
      'Textiles': { icon: Palette, color: '#b45309' },
      'Beads': { icon: Palette, color: '#b45309' },
      'Shirts': { icon: ShoppingBag, color: '#dc2626' },
      'Perfumes': { icon: Sparkles, color: '#be185d' },
      'Spirits': { icon: Wine, color: '#1e40af' },
      'Wine': { icon: Wine, color: '#1e40af' },
      'Beer': { icon: Wine, color: '#1e40af' },
      'Tobacco': { icon: Utensils, color: '#dc2626' },
      'Vapes': { icon: Utensils, color: '#dc2626' },
      'Rolling Papers': { icon: Utensils, color: '#dc2626' },
      'Glassware': { icon: GlassWater, color: '#06b6d4' },
      'Mixers': { icon: GlassWater, color: '#06b6d4' },
      'Grinders': { icon: Utensils, color: '#dc2626' },
      'Scales': { icon: Utensils, color: '#dc2626' },
      'Pipes': { icon: Utensils, color: '#dc2626' },
      'Bongs': { icon: Utensils, color: '#dc2626' },
      'Vaporizers': { icon: Utensils, color: '#dc2626' },
      'Lighters': { icon: Flame, color: '#dc2626' },
      'Ash Trays': { icon: Utensils, color: '#dc2626' },
      'Smoking Accessories': { icon: Utensils, color: '#dc2626' },
      'Whiskey': { icon: Wine, color: '#1e40af' },
      'Rum': { icon: Wine, color: '#1e40af' },
      'Vodka': { icon: Wine, color: '#1e40af' },
      'Gin': { icon: Wine, color: '#1e40af' },
      'Tequila': { icon: Wine, color: '#1e40af' },
      'Brandy': { icon: Wine, color: '#1e40af' },
      'Liqueur': { icon: Wine, color: '#1e40af' },
      'Champagne': { icon: Wine, color: '#1e40af' },
      'Cordials': { icon: Wine, color: '#1e40af' },
      'Cocktail Mixers': { icon: GlassWater, color: '#06b6d4' },
      'Bar Tools': { icon: Utensils, color: '#dc2626' },
      'Ice': { icon: GlassWater, color: '#06b6d4' },
      'Garnishes': { icon: Leaf, color: '#15803d' },
      'Doubles': { icon: Utensils, color: '#d97706' },
      'Chutney': { icon: Utensils, color: '#f97316' },
      'Buss Up Shut': { icon: Utensils, color: '#d97706' },
      'Dhal Puri': { icon: Utensils, color: '#d97706' },
      'Paratha': { icon: Utensils, color: '#d97706' },
      'Aloo Pie': { icon: Utensils, color: '#d97706' },
      'Beef Patty': { icon: Beef, color: '#b91c1c' },
      'Chicken Patty': { icon: Flame, color: '#dc2626' },
      'Veggie Patty': { icon: Leaf, color: '#15803d' },
      'Cheese Patty': { icon: Utensils, color: '#f97316' },
      'Patty': { icon: Utensils, color: '#d97706' },
      "Men's Cologne": { icon: Sparkles, color: '#be185d' },
      "Women's Perfume": { icon: Sparkles, color: '#be185d' },
      'Unisex Fragrance': { icon: Sparkles, color: '#be185d' },
      'Body Spray': { icon: Sparkles, color: '#be185d' },
      'Deodorant': { icon: Sparkles, color: '#be185d' },
      'Aftershave': { icon: Sparkles, color: '#be185d' },
      'Gift Sets': { icon: Sparkles, color: '#be185d' },
      'Travel Size': { icon: Sparkles, color: '#be185d' },
      'Designer Fragrance': { icon: Sparkles, color: '#be185d' },
      'Body Oils': { icon: Sparkles, color: '#be185d' },
      'Incense': { icon: Sparkles, color: '#be185d' },
      'Scented Candles': { icon: Sparkles, color: '#be185d' },
      'Rings': { icon: Sparkles, color: '#d97706' },
      'Necklaces': { icon: Sparkles, color: '#d97706' },
      'Bracelets': { icon: Sparkles, color: '#d97706' },
      'Earrings': { icon: Sparkles, color: '#d97706' },
      'Watches': { icon: Sparkles, color: '#d97706' },
      'Chains': { icon: Sparkles, color: '#d97706' },
      'Pendants': { icon: Sparkles, color: '#d97706' },
      'Engagement Rings': { icon: Sparkles, color: '#d97706' },
      'Wedding Bands': { icon: Sparkles, color: '#d97706' },
      'Gold Jewelry': { icon: Sparkles, color: '#d97706' },
      'Silver Jewelry': { icon: Sparkles, color: '#d97706' },
      'Diamond Jewelry': { icon: Sparkles, color: '#d97706' },
      'Costume Jewelry': { icon: Sparkles, color: '#d97706' },
      'Custom Designs': { icon: Sparkles, color: '#d97706' },
      'Jewelry Repair': { icon: Sparkles, color: '#d97706' },
      'Phones': { icon: Monitor, color: '#1e40af' },
      'Computers': { icon: Monitor, color: '#1e40af' },
      'Tablets': { icon: Monitor, color: '#1e40af' },
      'Laptops': { icon: Monitor, color: '#1e40af' },
      'TVs': { icon: Monitor, color: '#1e40af' },
      'Audio': { icon: Monitor, color: '#1e40af' },
      'Cameras': { icon: Monitor, color: '#1e40af' },
      'Gaming': { icon: Monitor, color: '#1e40af' },
      'Smart Home': { icon: Monitor, color: '#1e40af' },
      'Networking': { icon: Monitor, color: '#1e40af' },
      'Printers': { icon: Monitor, color: '#1e40af' },
      'Monitors': { icon: Monitor, color: '#1e40af' },
      'Phone Cases': { icon: Monitor, color: '#1e40af' },
      'Chargers': { icon: Monitor, color: '#1e40af' },
      'Charging Cables': { icon: Monitor, color: '#1e40af' },
      'Power Banks': { icon: Monitor, color: '#1e40af' },
      'Screen Protectors': { icon: Monitor, color: '#1e40af' },
      'Headphones': { icon: Monitor, color: '#1e40af' },
      'Earbuds': { icon: Monitor, color: '#1e40af' },
      'Smartwatches': { icon: Monitor, color: '#1e40af' },
      'Fitness Trackers': { icon: Monitor, color: '#1e40af' },
      'Microphones': { icon: Monitor, color: '#1e40af' },
      'Webcams': { icon: Monitor, color: '#1e40af' },
      'Bluetooth Speakers': { icon: Monitor, color: '#1e40af' },
      'Party Speakers': { icon: Monitor, color: '#1e40af' },
      'Gaming Accessories': { icon: Monitor, color: '#1e40af' },
      'Cables & Adapters': { icon: Monitor, color: '#1e40af' },
      'Memory Cards': { icon: Monitor, color: '#1e40af' },
      'USB Drives': { icon: Monitor, color: '#1e40af' },
      'Batteries': { icon: Monitor, color: '#1e40af' },
      'Streaming Devices': { icon: Monitor, color: '#1e40af' },
      'Home Automation': { icon: Monitor, color: '#1e40af' },
      'Tablet Accessories': { icon: Monitor, color: '#1e40af' },
      'Laptop Accessories': { icon: Monitor, color: '#1e40af' },
      'Appliances': { icon: Monitor, color: '#1e40af' },
      'Kitchen': { icon: Home, color: '#b45309' },
      'Tools': { icon: Home, color: '#b45309' },
      'Hardware': { icon: Home, color: '#b45309' },
      'Lighting': { icon: Home, color: '#b45309' },
      'Storage': { icon: Home, color: '#b45309' },
      'iPhone Screens': { icon: Monitor, color: '#1e40af' },
      'Android Screens': { icon: Monitor, color: '#1e40af' },
      'Tablet Screens': { icon: Monitor, color: '#1e40af' },
      'Engine Parts': { icon: Home, color: '#b45309' },
      'Brakes': { icon: Home, color: '#b45309' },
      'Suspension': { icon: Home, color: '#b45309' },
      'Exhaust': { icon: Home, color: '#b45309' },
      'Tires': { icon: Home, color: '#b45309' },
      'Wheels': { icon: Home, color: '#b45309' },
      'Motorcycle Tires': { icon: Home, color: '#b45309' },
      'Dirt Bike Tires': { icon: Home, color: '#b45309' },
      'Scooter Tires': { icon: Home, color: '#b45309' },
      'Truck Tires': { icon: Home, color: '#b45309' },
      'Decals': { icon: Home, color: '#b45309' },
      'Oil Filters': { icon: Home, color: '#b45309' },
      'Air Filters': { icon: Home, color: '#b45309' },
      'Spark Plugs': { icon: Home, color: '#b45309' },
      'Belts': { icon: Home, color: '#b45309' },
      'Hoses': { icon: Home, color: '#b45309' },
      'Radiators': { icon: Home, color: '#b45309' },
      'Alternators': { icon: Home, color: '#b45309' },
      'Car Starters': { icon: Home, color: '#b45309' },
      'Fuel Pumps': { icon: Home, color: '#b45309' },
      'Transmission': { icon: Home, color: '#b45309' },
      'Clutch': { icon: Home, color: '#b45309' },
      'Headlights': { icon: Home, color: '#b45309' },
      'Taillights': { icon: Home, color: '#b45309' },
      'Turn Signals': { icon: Home, color: '#b45309' },
      'Brake Lights': { icon: Home, color: '#b45309' },
      'Mirrors': { icon: Home, color: '#b45309' },
      'Wipers': { icon: Home, color: '#b45309' },
      'Car Battery': { icon: Home, color: '#b45309' },
      'Motor Oil': { icon: Home, color: '#b45309' },
      'Transmission Fluid': { icon: Home, color: '#b45309' },
      'Brake Fluid': { icon: Home, color: '#b45309' },
      'Coolant': { icon: Home, color: '#b45309' },
      'Power Steering Fluid': { icon: Home, color: '#b45309' },
      'Gear Oil': { icon: Home, color: '#b45309' },
      'Octane Boost': { icon: Home, color: '#b45309' },
      'Fuel Treatment': { icon: Home, color: '#b45309' },
      'Car Paint': { icon: Home, color: '#b45309' },
      'Car Primer': { icon: Home, color: '#b45309' },
      'Thinner': { icon: Home, color: '#b45309' },
      'Sandpaper': { icon: Home, color: '#b45309' },
      'Body Filler': { icon: Home, color: '#b45309' },
      'Spray Paint': { icon: Home, color: '#b45309' },
      'Clear Coat': { icon: Home, color: '#b45309' },
      'Rubbing Compound': { icon: Home, color: '#b45309' },
      'Polishing Compound': { icon: Home, color: '#b45309' },
      'Masking Tape': { icon: Home, color: '#b45309' },
      'Paint Brushes': { icon: Home, color: '#b45309' },
      'Paint Rollers': { icon: Home, color: '#b45309' },
      'Paint Trays': { icon: Home, color: '#b45309' },
      'Drop Cloths': { icon: Home, color: '#b45309' },
      'Paint Remover': { icon: Home, color: '#b45309' },
      'Paint Thinners': { icon: Home, color: '#b45309' },
      'Wall Putty': { icon: Home, color: '#b45309' },
      'Spackle': { icon: Home, color: '#b45309' },
      'Tape Measure': { icon: Home, color: '#b45309' },
      'Hammer': { icon: Home, color: '#b45309' },
      'Shovel': { icon: Home, color: '#b45309' },
      'Wicks': { icon: Home, color: '#b45309' },
      'Drill Sets': { icon: Home, color: '#b45309' },
      'Jack Hammers': { icon: Home, color: '#b45309' },
      'Screwdrivers': { icon: Home, color: '#b45309' },
      'Pliers': { icon: Home, color: '#b45309' },
      'Wrenches': { icon: Home, color: '#b45309' },
      'Saws': { icon: Home, color: '#b45309' },
      'Levels': { icon: Home, color: '#b45309' },
      'Ladders': { icon: Home, color: '#b45309' },
      'Toolboxes': { icon: Home, color: '#b45309' },
      'Gloves': { icon: Home, color: '#b45309' },
      'Safety Glasses': { icon: Home, color: '#b45309' },
      'Safety Vests': { icon: Home, color: '#b45309' },
      'Work Boots': { icon: Home, color: '#b45309' },
      'Hard Hats': { icon: Home, color: '#b45309' },
      'Ear Protection': { icon: Home, color: '#b45309' },
      'Dust Masks': { icon: Home, color: '#b45309' },
      'Respirators': { icon: Home, color: '#b45309' },
      'Brake Pads': { icon: Home, color: '#b45309' },
      'Brake Rotors': { icon: Home, color: '#b45309' },
      'Shocks': { icon: Home, color: '#b45309' },
      'Struts': { icon: Home, color: '#b45309' },
      'Wheel Bearings': { icon: Home, color: '#b45309' },
      'Fuel Injectors': { icon: Home, color: '#b45309' },
      'Ignition Coils': { icon: Home, color: '#b45309' },
      'OBD Scanners': { icon: Home, color: '#b45309' },
      'Seat Covers': { icon: Home, color: '#b45309' },
      'Floor Mats': { icon: Home, color: '#b45309' },
      'Car Alarms': { icon: Home, color: '#b45309' },
      'GPS Trackers': { icon: Home, color: '#b45309' },
      'Reverse Cameras': { icon: Home, color: '#b45309' },
      'Tire Repair Kits': { icon: Home, color: '#b45309' },
      'Motorcycle Batteries': { icon: Home, color: '#b45309' },
      'Brake Calipers': { icon: Home, color: '#b45309' },
      'Brake Lines': { icon: Home, color: '#b45309' },
      'Timing Belt': { icon: Home, color: '#b45309' },
      'Piston Rings': { icon: Home, color: '#b45309' },
      'Valve Cover Gasket': { icon: Home, color: '#b45309' },
      'Control Arms': { icon: Home, color: '#b45309' },
      'Sway Bar Links': { icon: Home, color: '#b45309' },
      'Spark Plug Wires': { icon: Home, color: '#b45309' },
      'Fuse Box': { icon: Home, color: '#b45309' },
      'Relay': { icon: Home, color: '#b45309' },
      'Fuel Filter': { icon: Home, color: '#b45309' },
      'Fuel Pressure Regulator': { icon: Home, color: '#b45309' },
      'Fan Clutch': { icon: Home, color: '#b45309' },
      'Transmission Mount': { icon: Home, color: '#b45309' },
      'Shift Solenoid': { icon: Home, color: '#b45309' },
      'Cabin Air Filter': { icon: Home, color: '#b45309' },
      'Spare Tire': { icon: Home, color: '#b45309' },
      'Tire Pressure Sensor': { icon: Home, color: '#b45309' },
      'Fog Lights': { icon: Home, color: '#b45309' },
      'LED Bulbs': { icon: Home, color: '#b45309' },
      'Washer Fluid': { icon: Home, color: '#b45309' },
      'Rear Bumper': { icon: Home, color: '#b45309' },
      'Hood': { icon: Home, color: '#b45309' },
      'Fender': { icon: Home, color: '#b45309' },
      'Steering Wheel': { icon: Home, color: '#b45309' },
      'Dashboard Cover': { icon: Home, color: '#b45309' },
      'Car Alarm': { icon: Home, color: '#b45309' },
      'Socket Set': { icon: Home, color: '#b45309' },
      'Wrench Set': { icon: Home, color: '#b45309' },
      'Car Cover': { icon: Home, color: '#b45309' },
      'Phone Mount': { icon: Home, color: '#b45309' },
      'Motorcycle Chain': { icon: Home, color: '#b45309' },
      'Motorcycle Brakes': { icon: Home, color: '#b45309' },
      'Alloy Wheels': { icon: Home, color: '#b45309' },
      'Steel Wheels': { icon: Home, color: '#b45309' },
      'Wheel Nuts': { icon: Home, color: '#b45309' },
      'Distributor Cap': { icon: Home, color: '#b45309' },
      'Oil Change Kit': { icon: Home, color: '#b45309' },
      'Air Filter Replacement': { icon: Home, color: '#b45309' },
      'Wiper Blades': { icon: Home, color: '#b45309' },
      'Tacos': { icon: Home, color: '#b45309' },
      'Burritos': { icon: Home, color: '#b45309' },
      'Nachos': { icon: Home, color: '#b45309' },
      'Enchiladas': { icon: Home, color: '#b45309' },
      'Fajitas': { icon: Home, color: '#b45309' },
      'Quesadillas': { icon: Home, color: '#b45309' },
      'Rotisserie Chicken': { icon: Home, color: '#b45309' },
      'Paella': { icon: Home, color: '#b45309' },
      'Empanadas': { icon: Home, color: '#b45309' },
      'Arepas': { icon: Home, color: '#b45309' },
      'Pupusas': { icon: Home, color: '#b45309' },
      'Arroz con Pollo': { icon: Home, color: '#b45309' },
      'Ropa Vieja': { icon: Home, color: '#b45309' },
      'Lechon Asado': { icon: Home, color: '#b45309' },
      'Yuca Fries': { icon: Home, color: '#b45309' },
      'Plantains': { icon: Home, color: '#b45309' },
      'Maduros': { icon: Home, color: '#b45309' },
      'Tostones': { icon: Home, color: '#b45309' },
      'Mofongo': { icon: Home, color: '#b45309' },
      'Sancocho': { icon: Home, color: '#b45309' },
      'Chicharron': { icon: Home, color: '#b45309' },
      'Pernil': { icon: Home, color: '#b45309' },
      'Bandeja Paisa': { icon: Home, color: '#b45309' },
      'Lomo Saltado': { icon: Home, color: '#b45309' },
      'Pollo a la Brasa': { icon: Home, color: '#b45309' },
      'Guacamole': { icon: Home, color: '#b45309' },
      'Salsa': { icon: Home, color: '#b45309' },
      'Tortillas': { icon: Home, color: '#b45309' },
      'Mexican Rice': { icon: Home, color: '#b45309' },
      'Refried Beans': { icon: Home, color: '#b45309' },
      'Churros': { icon: Home, color: '#b45309' },
      'Margaritas': { icon: Home, color: '#b45309' },
      'Mexican Coke': { icon: Home, color: '#b45309' },
      'Horchata': { icon: Home, color: '#b45309' },
      'Mole': { icon: Home, color: '#b45309' },
      'Ceviche': { icon: Home, color: '#b45309' },
      'Carnitas': { icon: Home, color: '#b45309' },
      'Carne Asada': { icon: Home, color: '#b45309' },
      'Pollo Asado': { icon: Home, color: '#b45309' },
      'Pozole': { icon: Home, color: '#b45309' },
      'Menudo': { icon: Home, color: '#b45309' },
      'Tortas': { icon: Home, color: '#b45309' },
      'Tostadas': { icon: Home, color: '#b45309' },
      'Elote': { icon: Home, color: '#b45309' },
      'Esquites': { icon: Home, color: '#b45309' },
      'Appliances': { icon: Home, color: '#b45309' },
      'Kitchen': { icon: Home, color: '#b45309' },
      'Furniture': { icon: Home, color: '#b45309' },
      'Bedding': { icon: Home, color: '#b45309' },
      'Bathroom': { icon: Home, color: '#b45309' },
      'Tools': { icon: Home, color: '#b45309' },
      'Hardware': { icon: Home, color: '#b45309' },
      'Lighting': { icon: Home, color: '#b45309' },
      'Storage': { icon: Home, color: '#b45309' },
      'Decor': { icon: Home, color: '#b45309' },
      'Screws': { icon: Home, color: '#b45309' },
      'Fasteners': { icon: Home, color: '#b45309' },
      'Bolts': { icon: Home, color: '#b45309' },
      'Nuts': { icon: Home, color: '#b45309' },
      'Washers': { icon: Home, color: '#b45309' },
      'Nails': { icon: Home, color: '#b45309' },
      'Anchors': { icon: Home, color: '#b45309' },
      'Hinges': { icon: Home, color: '#b45309' },
      'Latches': { icon: Home, color: '#b45309' },
      'Handles': { icon: Home, color: '#b45309' },
      'Knobs': { icon: Home, color: '#b45309' },
      'Locks': { icon: Home, color: '#b45309' },
      'Keys': { icon: Home, color: '#b45309' },
      'Rope': { icon: Home, color: '#b45309' },
      'Chain': { icon: Home, color: '#b45309' },
      'Wire': { icon: Home, color: '#b45309' },
      'Cable': { icon: Home, color: '#b45309' },
      'Tape': { icon: Home, color: '#b45309' },
      'Adhesives': { icon: Home, color: '#b45309' },
      'Sealants': { icon: Home, color: '#b45309' },
      'Pin': { icon: Home, color: '#b45309' },
      'House Paint': { icon: Home, color: '#b45309' },
      'Primer': { icon: Home, color: '#b45309' },
      'House Paint Brushes': { icon: Home, color: '#b45309' },
      'House Paint Rollers': { icon: Home, color: '#b45309' },
      'Paint Trays': { icon: Home, color: '#b45309' },
      'Drop Cloths': { icon: Home, color: '#b45309' },
      'Paint Remover': { icon: Home, color: '#b45309' },
      'Paint Thinners': { icon: Home, color: '#b45309' },
      'Wall Putty': { icon: Home, color: '#b45309' },
      'Spackle': { icon: Home, color: '#b45309' },
      'Tape Measure': { icon: Home, color: '#b45309' },
      'Hammer': { icon: Home, color: '#b45309' },
      'Shovel': { icon: Home, color: '#b45309' },
      'Wicks': { icon: Home, color: '#b45309' },
      'Drill Sets': { icon: Home, color: '#b45309' },
      'Jack Hammers': { icon: Home, color: '#b45309' },
      'Screwdrivers': { icon: Home, color: '#b45309' },
      'Pliers': { icon: Home, color: '#b45309' },
      'Wrenches': { icon: Home, color: '#b45309' },
      'Saws': { icon: Home, color: '#b45309' },
      'Levels': { icon: Home, color: '#b45309' },
      'Ladders': { icon: Home, color: '#b45309' },
      'Toolboxes': { icon: Home, color: '#b45309' },
      'Gloves': { icon: Home, color: '#b45309' },
      'Safety Glasses': { icon: Home, color: '#b45309' },
      'Safety Vests': { icon: Home, color: '#b45309' },
      'Work Boots': { icon: Home, color: '#b45309' },
      'Hard Hats': { icon: Home, color: '#b45309' },
      'Ear Protection': { icon: Home, color: '#b45309' },
      'Dust Masks': { icon: Home, color: '#b45309' },
      'Respirators': { icon: Home, color: '#b45309' },
      'Bottled Water': { icon: GlassWater, color: '#06b6d4' },
      'Litre Bottle': { icon: GlassWater, color: '#06b6d4' },
      'Spring Water': { icon: GlassWater, color: '#06b6d4' },
      'Distilled Water': { icon: GlassWater, color: '#06b6d4' },
      'Mineral Water': { icon: GlassWater, color: '#06b6d4' },
      'Flavored Water': { icon: GlassWater, color: '#06b6d4' },
      'Sparkling Water': { icon: GlassWater, color: '#06b6d4' },
      'Water Coolers': { icon: GlassWater, color: '#06b6d4' },
      'Water Dispensers': { icon: GlassWater, color: '#06b6d4' },
      'Water Delivery': { icon: GlassWater, color: '#06b6d4' },
      'Water Cups': { icon: GlassWater, color: '#06b6d4' },
      'Ice': { icon: GlassWater, color: '#06b6d4' }
    };
    return iconMap[category] || { icon: Utensils, color: '#6b7280' };
  };

  const categoryInfo = getCategoryIcon(category);
  const IconComponent = categoryInfo.icon;

  useEffect(() => {
    if (!firebaseError && db) {
      loadMenuItems();
    } else {
      setLoading(false);
      setError('Failed to load items');
    }
  }, [firebaseError, db, category, restaurantIdParam]);

  const loadMenuItems = async () => {
    setLoading(true);
    setError('');

    try {
      // If restaurantId is provided, only load from that specific restaurant
      if (restaurantIdParam) {
        // Load the specific restaurant
        const restaurantRef = doc(db, 'restaurants', restaurantIdParam);
        const restaurantDoc = await getDoc(restaurantRef);
        
        if (restaurantDoc.exists()) {
          const restaurantData = { id: restaurantDoc.id, ...restaurantDoc.data() };
          setRestaurants([restaurantData]);
          
          // Load menu items from this specific restaurant
          const menuItemsRef = collection(db, 'restaurants', restaurantIdParam, 'menuItems');
          const menuItemsSnapshot = await getDocs(menuItemsRef);
          const restaurantMenuItems = menuItemsSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              restaurantId: restaurantIdParam,
              restaurantName: restaurantData.name,
              restaurantBannerURL: restaurantData.bannerURL
            }))
            .filter(item => item.category === category);
          setMenuItems(restaurantMenuItems);
        } else {
          setError('Restaurant not found');
          setMenuItems([]);
        }
      } else {
        // Load all restaurants (original behavior when no restaurantId is provided)
        const restaurantsRef = collection(db, 'restaurants');
        const restaurantsSnapshot = await getDocs(restaurantsRef);
        const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRestaurants(restaurantsData);

        // Load menu items from all restaurants that match the category
        const allMenuItems = [];
        for (const restaurant of restaurantsData) {
          try {
            const menuItemsRef = collection(db, 'restaurants', restaurant.id, 'menuItems');
            const menuItemsSnapshot = await getDocs(menuItemsRef);
            const restaurantMenuItems = menuItemsSnapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
                restaurantId: restaurant.id,
                restaurantName: restaurant.name,
                restaurantBannerURL: restaurant.bannerURL
              }))
              .filter(item => item.category === category);
            allMenuItems.push(...restaurantMenuItems);
          } catch (error) {
            console.warn(`Could not load menu items for restaurant ${restaurant.id}:`, error);
          }
        }

        setMenuItems(allMenuItems);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading menu items:', err);
      setError('Failed to load items');
      setLoading(false);
    }
  };

  const handleToggleFavorite = (e, item) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    toggleFavorite({
      id: item.id,
      name: item.name,
      description: item.itemDescription,
      imageURL: item.menuItemImageURL,
      restaurantId: item.restaurantId
    });
  };

  const handleAddToCart = (e, item) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (isAppOffline) {
      window.alert("We're currently offline. Please check back later.");
      return;
    }
    if (item.trackStock === true && (item.stockCount ?? 0) <= 0) return;
    if (Array.isArray(item.variants) && item.variants.length > 0) {
      setSelectedItem(item);
      return;
    }

    const restaurant = restaurants.find(r => r.id === item.restaurantId);
    if (restaurant) {
      setRestaurant(restaurant);
    }

    addToCart(item, 1, restaurant?.bannerURL || restaurant?.menuImageURL);
    setToastMessage(`${item.name} added to cart`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const filteredItems = menuItems.filter(item => {
    const matchesSearch = !searchText || 
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (item.itemDescription && item.itemDescription.toLowerCase().includes(searchText.toLowerCase()));
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-3 py-20">
        <div className="w-8 h-8 rounded-full border-[3px] border-gray-200 border-t-[#FF3B30] animate-spin" />
        <p className="text-[15px] text-[#8E8E93]">Loading items...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-4 py-20 px-8">
        <UtensilsCrossed size={48} className="text-gray-300" strokeWidth={1.5} />
        <h2 className="text-[20px] font-semibold text-gray-900">{error}</h2>
        <button onClick={() => navigate('/dashboard/menu')} className="text-[15px] font-semibold text-white bg-red-600 px-5 py-2.5 rounded-xl">
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#F2F2F7] min-h-full pb-28">
      {/* iOS nav bar — back chevron + centered category title */}
      <div className="sticky top-0 z-30 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
        <div className="h-12 flex items-center px-1 relative">
          <button
            onClick={() => navigate(restaurantIdParam
              ? `/restaurant/${restaurantIdParam}?businessType=${businessTypeParam}`
              : '/dashboard/menu')}
            className="p-2" aria-label="Back"
          >
            <ChevronLeft size={24} className="text-gray-800" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[17px] font-semibold text-gray-900 max-w-[60%]">
            <IconComponent size={18} style={{ color: categoryInfo.color }} className="shrink-0" />
            <span className="truncate">{category}</span>
          </h1>
          <div className="flex-1" />
        </div>
      </div>

      {/* Main content with sidebar */}
      <div className="flex gap-4 px-[7px] pt-3 max-w-[1400px] mx-auto">
        {/* Main content area */}
        <div className="flex-1 min-w-0">
          {/* iOS search field */}
          <div className="flex items-center gap-2 bg-[#E5E5EA] rounded-[10px] px-3 py-2 mb-2">
            <Search size={17} className="text-[#8E8E93] shrink-0" />
            <input
              type="text"
              placeholder={`Search ${category.toLowerCase()}...`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-[15px] text-gray-900 placeholder-[#8E8E93] focus:outline-none"
            />
            {searchText !== '' && (
              <button onClick={() => setSearchText('')} aria-label="Clear search">
                <CircleX size={17} className="text-[#8E8E93]" />
              </button>
            )}
          </div>

          {/* Results Count */}
          <p className="px-1 py-2 text-[13px] text-[#8E8E93]">
            {filteredItems.length} items found
          </p>

          {/* Menu Items Grid - responsive layout */}
          {filteredItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filteredItems.map(item => {
              const restaurant = restaurants.find(r => r.id === item.restaurantId);
              const imageUrl = item.menuItemImageURL || (restaurant?.bannerURL && restaurant.bannerURL !== '' ? restaurant.bannerURL : '/placeholder-food.jpg');

              return (
                <div
                  key={item.id}
                  ref={el => { itemRefs.current[item.id] = el; }}
                  className="cursor-pointer bg-white rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5 active:scale-[0.98] transition-transform"
                  onClick={() => setSelectedItem(item)}
                >
                  {/* Image Section with heart and plus overlay */}
                  <div className="relative w-full h-[120px] bg-gray-100">
                    {/* Fit inside the frame (no crop); blurred cover of the
                        same image fills the letterbox for portrait uploads */}
                    <img
                      src={imageUrl}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
                    />
                    <img
                      src={imageUrl}
                      alt={item.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain"
                      onError={(e) => { e.target.src = '/placeholder-food.jpg'; }}
                    />
                    {/* Heart button overlay on image */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(e, item);
                      }}
                      className="absolute top-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center z-10"
                      aria-label="Favorite"
                    >
                      <Heart
                        size={14}
                        className={isFavorite(item.id) ? 'text-[#FF3B30]' : 'text-white'}
                        fill={isFavorite(item.id) ? '#FF3B30' : 'none'}
                      />
                    </button>

                    {/* Out of Stock badge */}
                    {item.trackStock === true && (item.stockCount ?? 0) <= 0 && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                        <span className="px-2.5 py-1 rounded-lg bg-black/70 text-white text-[10px] font-bold uppercase tracking-wide">
                          Out of Stock
                        </span>
                      </div>
                    )}

                    {/* Plus button for quick add to cart */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(e, item);
                      }}
                      disabled={item.trackStock === true && (item.stockCount ?? 0) <= 0}
                      className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md z-10 ${item.trackStock === true && (item.stockCount ?? 0) <= 0 ? 'bg-gray-400' : 'bg-[#FF3B30]'}`}
                      aria-label="Add to cart"
                    >
                      <Plus size={16} className="text-white" />
                    </button>
                  </div>

                  {/* Content Section - Under image */}
                  <div className="p-2">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-xs font-semibold text-gray-900 truncate flex-1">
                        {item.name}
                      </h3>
                      <span className="text-xs font-bold text-[#30D158] shrink-0">
                        ${item.price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          ) : (
            <div className="py-16 text-center flex flex-col items-center gap-3">
              <IconComponent size={48} style={{ color: '#9ca3af' }} />
              <h2 className="text-[17px] font-bold text-gray-900">
                No Items Found
              </h2>
              <p className="text-[15px] text-[#8E8E93]">
                {searchText ? 'Try adjusting your search terms' : 'Check back later for new items'}
              </p>
              <button onClick={() => navigate('/dashboard/menu')} className="mt-4 text-[15px] font-semibold text-white bg-red-600 px-5 py-2.5 rounded-xl">
                Browse Other Categories
              </button>
            </div>
          )}
        </div>
        {/* Item name sidebar — right side, like iOS */}
        <aside className={`hidden lg:block flex-shrink-0 transition-all duration-300 ${isSidebarCollapsed ? 'w-12' : 'w-64'}`}>
          <div className="sticky top-16 bg-white rounded-xl shadow-sm max-h-[calc(100vh-8rem)] overflow-y-auto">
            {/* Collapse toggle button */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="w-full flex items-center justify-center p-3 border-b border-black/[0.06]"
            >
              {isSidebarCollapsed ? (
                <ChevronLeft size={18} className="text-gray-600" />
              ) : (
                <ChevronRight size={18} className="text-gray-600" />
              )}
            </button>

            {/* Content */}
            {!isSidebarCollapsed ? (
              <div className="p-3">
                <h3 className="text-xs font-semibold text-[#8E8E93] mb-3 uppercase tracking-wide">All Items ({filteredItems.length})</h3>
                <nav className="space-y-1 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  {filteredItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => {
                        const el = itemRefs.current[item.id];
                        if (el) {
                          const top = el.getBoundingClientRect().top + window.scrollY - 140;
                          window.scrollTo({ top, behavior: 'smooth' });
                        }
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg text-[14px] leading-snug transition-colors text-gray-700 active:bg-gray-100 break-words"
                    >
                      {item.name}
                    </button>
                  ))}
                </nav>
              </div>
            ) : (
              <div className="p-2">
                <button
                  onClick={() => setIsSidebarCollapsed(false)}
                  className="w-full flex flex-col items-center gap-1 p-2 rounded-lg active:bg-gray-100 transition-colors"
                  title="Open menu"
                >
                  <Menu size={20} className="text-gray-600" />
                  <span className="text-xs text-[#8E8E93]">Items</span>
                </button>
                <p className="text-xs text-[#8E8E93] text-center mt-2 leading-tight">Tap to open</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Menu Item Detail Modal */}
      {selectedItem && (
        <MenuItemDetailModal
          item={selectedItem}
          restaurant={restaurants.find(r => r.id === selectedItem.restaurantId)}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Floating cart FAB — iOS bottomTrailing */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setShowCartSheet(true)}
          className="fixed bottom-[100px] right-4 z-50 w-14 h-14 rounded-full bg-[#FF3B30] shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Open cart"
        >
          <ShoppingCart size={24} className="text-white" />
          {getItemCount() > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#34C759] text-white text-xs font-bold flex items-center justify-center">
              {getItemCount() > 99 ? '99+' : getItemCount()}
            </span>
          )}
        </button>
      )}

      {/* Cart bottom sheet — replaces right-side drawer */}
      <CartSheet open={showCartSheet} onClose={() => setShowCartSheet(false)} />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10001] bg-gray-900/90 text-white text-[15px] px-4 py-2.5 rounded-full flex items-center gap-2 shadow-lg">
          <CheckCircle size={16} className="text-[#30D158]" />
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default CategoryDetailPage;
